import React, { useState, useEffect, useRef, useMemo } from "react";
import { appsignal } from "./../../appsignal";
import RiskBanner from "@appComponents/RiskBanner/RiskBanner";
import { ExploreDropdown, ProductsDropdown } from "./components/Dropdowns";
import classNames from "classnames";
import { ErrorBoundary } from "@appsignal/react";
import ChevronDownBlackIcon from "./icons/chevron-down-black.svg";
import CloseIcon from "./images/ico-close.svg";
import LogoColoredIcon from "./icons/logo-colored.svg";
import IcoArrow from "./images/ico-arrow.svg";
import IcoProfile from "./images/ico-profile.svg";
import IcoArrowCorner from "./images/ico-arrow-corner.svg";
import IcoTick from "./images/ico-tick.svg";
import IcoLogout from "./images/ico-logout.svg";
import { useMediaQuery } from "react-responsive";
import { AccountTier, BFID_URL, DESKTOP_WIDTH } from "@bundles/consts";
import { getIsRouteActive, getProfileSubMenuLinks, MenusConfig } from "./const";
import { TierBadge } from "@appComponents/TierBadge/TierBadge";
import ProfileService from "@services/Profile.service";
import { isTier1AndAbove } from "@funds/features/helpers/functions";
import PortalRender from "@appComponents/PortalRender/PortalRender";

const style = require("./MenuAppStyle.module.scss");

type Submenu = "products" | "explore";

type MenuAppProps = {
  user: {
    country?: string;
    slug?: string;
    name?: string;
    avatar: string;
    userSignedIn?: boolean;
    tier?: AccountTier;
    step?: number;
  };
};

const MenuLink = ({
  href,
  label,
  external = false,
}: {
  href: string;
  label: string;
  external?: boolean;
}) => {
  const isActive = getIsRouteActive(href);

  return external ? (
    <a
      className={classNames("font-semibold flex items-center h-full", {
        "text-[#81109c] border-b-2": isActive,
      })}
      href={href}
      target="_blank"
      rel="noopener noreferrer"
    >
      {label}
    </a>
  ) : (
    <a
      data-cy={`menu-link-${label}`}
      className={classNames("font-semibold flex items-center h-full", {
        "text-[#81109c] border-b-2": isActive,
      })}
      href={href}
    >
      {label}
    </a>
  );
};

const DropdownToggle = ({
  menu,
  label,
  dropdown,
  toggleDropdown,
}: {
  menu: Submenu;
  label: string;
  dropdown: Submenu | undefined;
  isHovered?: boolean;
  toggleDropdown: (menu: Submenu) => void;
}) => (
  <div
    className={classNames(
      "flex items-center gap-2 cursor-pointer transition-all",
      {
        "border-b-2 border-success": dropdown === menu,
        "hover:border-b-2 hover:border-success": dropdown !== menu,
      }
    )}
    onClick={() => toggleDropdown(menu)}
  >
    {label}
    <ChevronDownBlackIcon
      className={classNames({
        "rotate-180": dropdown === menu,
      })}
    />
  </div>
);

const MenuApp = ({ user }: MenuAppProps) => {
  const submenuButtonRef = useRef<HTMLDivElement | null>(null);

  const [dropdown, setDropdown] = useState<Submenu | undefined>();
  const [isSubmenuOpen, setIsSubmenuOpen] = useState<Submenu | undefined>();
  const [isDropdownVisible, setIsDropdownVisible] = useState(false);
  const [isHovered, setIsHovered] = useState(false);
  const [profileSubmenuClass, setProfileSubmenuClass] = useState("");
  const [shouldDisplayOverlay, setShouldDisplayOverlay] = useState(false);
  const [menusConfig, setMenusConfig] = useState<MenusConfig | null>(null);
  const [submenuTop, setSubmenuTop] = useState(50);

  const isMobile = useMediaQuery({ maxWidth: DESKTOP_WIDTH });

  const dropdownRef = useRef<HTMLDivElement>(null);

  const profileSubMenuLinks = useMemo(
    () =>
      getProfileSubMenuLinks({
        slug: user.slug,
        isMobile,
        menusConfig,
        step: user.step ?? 0,
      }),
    [user.slug, isMobile, menusConfig, user.step]
  );

  const isUk = user.country === "GB";

  const toggleDropdown = (menu: Submenu) => {
    if (dropdown === menu) {
      setDropdown(undefined);
      setIsDropdownVisible(false);
    } else {
      setDropdown(menu);
      setIsDropdownVisible(true);
    }
  };

  const openSubmenu = (menu: Submenu) => {
    window.scrollTo({ top: 0, behavior: "smooth" });
    setIsSubmenuOpen(menu);
  };

  const closeSubmenu = () => {
    setIsSubmenuOpen(undefined);
  };

  const bodyListener = (e: MouseEvent) => {
    const target = e.target as HTMLElement;
    const submenu = document.querySelector(`.${style.profileSubmenu}`);
    const submenuButton = document.getElementById("probileSubmenuButton");

    if (
      submenu &&
      !submenu.contains(target) &&
      !(target === submenuButton || submenuButton.contains(target))
    ) {
      setProfileSubmenuClass("");
      setIsSubmenuOpen(undefined);
    }
  };

  const profileSubmenuClick = () => {
    setProfileSubmenuClass((prev) => (!prev ? "active" : ""));
  };

  const handleMouseEnter = () => {
    setIsHovered(true);
  };

  const handleMouseLeave = () => {
    setIsHovered(false);
  };

  const fetchUserData = async () => {
    const data = await ProfileService.fetchUserData();

    setMenusConfig(data?.data?.menus);
  };

  useEffect(() => {
    document.body.addEventListener("click", bodyListener);

    return () => {
      document.body.removeEventListener("click", bodyListener);
    };
  }, []);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setDropdown(undefined);
        setIsDropdownVisible(false);
      }
    };

    if (isDropdownVisible) {
      document.addEventListener("mousedown", handleClickOutside);
    } else {
      document.removeEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isDropdownVisible]);

  useEffect(() => {
    if (isMobile) {
      if (profileSubmenuClass === "active") {
        document.body.style.overflow = "hidden";
        setShouldDisplayOverlay(true);
      } else {
        document.body.style.overflow = "auto";
        setShouldDisplayOverlay(false);
      }
    }

    return () => {
      document.body.style.overflow = "auto";
      setShouldDisplayOverlay(false);
    };
  }, [isMobile, profileSubmenuClass]);

  useEffect(() => {
    if (user.userSignedIn) {
      fetchUserData();
    }
  }, [user.userSignedIn]);

  useEffect(() => {
    if (submenuButtonRef.current) {
      const rect = submenuButtonRef.current.getBoundingClientRect();
      setSubmenuTop(rect.bottom);
    }
  }, [profileSubmenuClass]);

  const menuItems = user.userSignedIn ? (
    <>
      <li className="h-full flex items-center">
        <MenuLink
          href={`/profile/${user.slug}/membership`}
          label={user?.step > 0 ? "Membership" : "Start"}
        />
      </li>
      <li className="h-full flex items-center">
        <MenuLink
          href={`/profile/${user.slug}/funds/myFunds`}
          label="My Funds"
        />
      </li>
      <li className="h-full flex items-center">
        <MenuLink href={`/profile/${user.slug}/shares`} label="My Shares" />
      </li>
      <li className="h-full flex items-center">
        <MenuLink href="/market/" label="Markets" />
      </li>
      <li className="h-full flex items-center">
        <MenuLink
          href={`/profile/${user.slug}/funds/stakingEth`}
          label="Staking"
        />
      </li>
    </>
  ) : (
    <>
      <li>
        <DropdownToggle
          menu="products"
          label="Our Products"
          dropdown={dropdown}
          toggleDropdown={toggleDropdown}
          isHovered={isHovered || isDropdownVisible}
        />
      </li>
      <li className="w-full lg:w-auto">
        <DropdownToggle
          menu="explore"
          label="Explore"
          dropdown={dropdown}
          toggleDropdown={toggleDropdown}
          isHovered={isHovered || isDropdownVisible}
        />
      </li>
      <li>
        <MenuLink href="/blog" label="Announcements" external />
      </li>
      <li>
        <MenuLink href="/support" label="Support" external />
      </li>
    </>
  );

  const renderSubmenuListItems = () =>
    isSubmenuOpen ? (
      <>
        <div
          onClick={closeSubmenu}
          className="text-black mb-8 flex items-center cursor-pointer"
        >
          <ChevronDownBlackIcon
            width={12}
            height={12}
            className="rotate-[90deg] mr-2"
          />
          Back
        </div>

        {isSubmenuOpen === "products" && (
          <div className="text-left">
            <div className="text-xs text-primary-dark mb-8 uppercase">
              Our Products
            </div>
            <ProductsDropdown isUk={isUk} isMobileView />
          </div>
        )}
        {isSubmenuOpen === "explore" && (
          <div className="text-left">
            <div className="text-xs text-primary-dark mb-8 uppercase">
              Explore
            </div>
            <ExploreDropdown
              isUk={isUk}
              userSignedIn={user.userSignedIn}
              isMobileView
            />
          </div>
        )}
      </>
    ) : (
      <>
        <div
          data-cy="submenu-item-userName"
          className="flex items-center justify-between text-[16px] pb-[11px]"
        >
          <div className="flex items-center gap-[15px">
            <IcoProfile />
            <span className="text-[16px] font-medium">{user.name}</span>
          </div>

          {isMobile && (
            <div
              onClick={() => setProfileSubmenuClass("")}
              className="h-[16px]"
            >
              <CloseIcon />
            </div>
          )}
        </div>

        <div className="max-h-[90vh] overflow-y-auto border-t border-b lg:border-t-0 border-grey-lighter">
          <div
            data-cy="submenu-item-bf-id"
            className={classNames("bg-[#F6F6F6] rounded-[10px] mb-2 p-0")}
          >
            <div
              style={{ display: "flex" }}
              data-cy="submenu-item-bfid-status"
              className="items-center justify-between py-6 mx-4 border-b border-[#E4E4E4]"
            >
              <div className="text-xs">Status:</div>
              <TierBadge tier={user.tier} isSmall />
            </div>
            <a
              style={{ display: "flex" }}
              data-cy="bf-id-link"
              className="items-center justify-between p-[11px]"
              href={BFID_URL}
            >
              BF Identity <IcoArrowCorner />
            </a>
          </div>

          {profileSubMenuLinks.map(({ label, href, soon }) => {
            const isActive = getIsRouteActive(href);

            return (
              <li
                data-cy={`submenu-item-${label}`}
                className={classNames(style.item, {
                  "bg-[#F6F6F6] rounded-[10px]": isActive,
                })}
              >
                <a href={href}>
                  {soon ? (
                    <>
                      <span className="text-grey-lightest mr-[8px]">
                        {label}
                      </span>
                      <span className="uppercase text-white bg-primary-regular text-xs px-[8px] py-[3px] rounded-[100px]">
                        SOON
                      </span>
                    </>
                  ) : (
                    <span
                      style={{ display: "flex" }}
                      className="items-center justify-between"
                    >
                      {label}
                      {isActive && <IcoTick />}
                    </span>
                  )}
                </a>
              </li>
            );
          })}

          <li
            data-cy="submenu-item-Our-Products"
            className="cursor-pointer flex justify-between items-center p-[11px] border-t border-[#E4E4E4]"
            onClick={() => openSubmenu("products")}
          >
            Our Products{" "}
            <ChevronDownBlackIcon
              width={12}
              height={12}
              className="rotate-[-90deg] ml-3"
            />
          </li>
          <li
            data-cy="submenu-item-Explore"
            className="cursor-pointer flex justify-between items-center p-[11px]"
            onClick={() => openSubmenu("explore")}
          >
            Explore{" "}
            <ChevronDownBlackIcon
              width={12}
              height={12}
              className="rotate-[-90deg] ml-3"
            />
          </li>

          <li
            data-cy="submenu-item-Announcements"
            className={classNames(style.item, "border-t border-[#E4E4E4]")}
          >
            <a
              style={{ display: "flex" }}
              className="items-center justify-between"
              href="/blog"
            >
              Announcements <IcoArrowCorner />
            </a>
          </li>
        </div>

        <li data-cy="submenu-item-Logout" className="pt-[11px]">
          <a
            href="/users/sign_out"
            className="flex py-2 px-5 h-fit pointer rounded-[8px] text-white text-sm bg-success items-center gap-2 justify-center"
          >
            Log out
            <IcoLogout />
          </a>
        </li>
      </>
    );

  const renderProfilesOption = () => (
    <>
      <div
        className={classNames(
          style.menuItem,
          style.profileOptions,
          "js-class-helper"
        )}
      >
        <div
          id="probileSubmenuButton"
          data-cy="profile-menu-button"
          ref={submenuButtonRef}
          className={classNames(style.name, "js-class-helper")}
          onClick={profileSubmenuClick}
        >
          <IcoProfile />
          <IcoArrow
            className={classNames({
              "rotate-180": !!profileSubmenuClass,
            })}
          />
        </div>
        <PortalRender portalId="sidemenu-container">
          <ul
            style={{
              top: isMobile ? (submenuTop > 50 ? 50 : submenuTop) : submenuTop,
            }}
            className={classNames(
              style.profileSubmenu,
              style[profileSubmenuClass],
              "js-class-helper"
            )}
          >
            {renderSubmenuListItems()}
          </ul>
        </PortalRender>
      </div>
    </>
  );

  return (
    <>
      <RiskBanner />

      <ErrorBoundary instance={appsignal}>
        <div className="tailwind">
          <header
            className={classNames(
              style.header,
              style.menu,
              "flex opacity-100 bg-white justify-between items-center px-5 w-full top-0 left-0 z-50 text-sm sticky top-0"
            )}
            onMouseEnter={handleMouseEnter}
            onMouseLeave={handleMouseLeave}
          >
            {shouldDisplayOverlay && (
              <div
                style={{ position: "fixed" }}
                className="top-0 right-0 bottom-0 left-0 bg-[rgba(0,0,0,0.3)]"
              />
            )}
            <div className="flex justify-between items-center w-full mx-auto max-w-[1440px]">
              <a className="pt-5 pb-6" href="/">
                <LogoColoredIcon width={155} height={30} />
              </a>

              {!isMobile && (
                <nav className="flex">
                  <ul
                    style={{ paddingLeft: 0 }}
                    className={classNames(
                      "flex items-center list-none gap-[40px] text-black h-[64px]"
                    )}
                  >
                    {menuItems}
                  </ul>
                </nav>
              )}

              <div className="pt-5 pb-6">
                {user.userSignedIn ? (
                  renderProfilesOption()
                ) : (
                  <div className="flex gap-3 md:flex-row w-full md:w-auto">
                    <a
                      href={`//${process.env.DOMAIN}/join`}
                      className="py-2 px-5 h-fit pointer rounded-[12px] text-center text-sm text-black border border-success"
                    >
                      Join Us
                    </a>
                    <a
                      href={`//${process.env.DOMAIN}/users/sign_in`}
                      className="py-2 px-5 h-fit pointer rounded-[12px] text-center text-white text-sm bg-success"
                    >
                      Sign in
                    </a>
                  </div>
                )}
              </div>
            </div>
          </header>

          {isDropdownVisible && (
            <div
              className="fixed inset-0 bg-typo-dark opacity-30 z-40"
              onClick={() => setDropdown(undefined)}
            ></div>
          )}

          <style>{`
          @keyframes slide-down {
            0% {
              opacity: 0;
              transform: translateY(-20px);
            }
            100% {
              opacity: 1;
              transform: translateY(0);
            }
          }
        `}</style>

          {!isMobile && dropdown && isDropdownVisible && (
            <div
              className="absolute z-[1000] top-[67px] left-0 w-full bg-primary-darkest shadow-sm animate-slide-down block"
              ref={dropdownRef}
            >
              {dropdown === "products" && (
                <ProductsDropdown isUk={isUk} slug={user.slug} />
              )}
              {dropdown === "explore" && (
                <ExploreDropdown
                  isUk={isUk}
                  userSignedIn={user.userSignedIn}
                  slug={user.slug}
                />
              )}
            </div>
          )}
        </div>
      </ErrorBoundary>
    </>
  );
};

export default (props: MenuAppProps) => <MenuApp {...props} />;
